import React, { useState, useEffect, createContext, useRef } from "react";
import { observer } from "mobx-react";
import {
  writingAssistantStoreData,
  defaultInputValue
} from "Stores/WritingAssistantData";
import WritingAssistantUtils from "Utils/WritingAssistantUtils";
import userDetail from "Stores/userDetail";
import Utils from "Utils/utils";
import { MXP_EVENT } from "Static/MixPanelEvents";
import RewriteAiInputCard from "./RewriteAiInputCard";
import WandButton from "./WandButton";
import ScoreBot from "./ScoreBot";
import ChatGptLoader from "./ChatGptLoader";
import { toasterState } from "Components/common/toaster";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { reaction } from "mobx";
import copilotDrawerStore from "Stores/copilotDrawerStore";
import copilotEditorStore, { COMPOSE_IN_COPILOT } from "Stores/copilotEditorStore";

const RewriteAiContext = createContext({});

const RewriteAi = observer(
  ({
    fromTaskPage = false,
    editorRef = null,
    pageType = "",
    updateContentCbk = () => {},
    emailType = "",
    emailSubject = "",
    replyAsThread = false,
    composeLabel = ""
  }) => {
    const {
      writingAssistantEnabled = false,
      inFreePlan = false,
      planName = ""
    } = userDetail?.userFeatureFlag || {};

    const inputBoxRef = useRef(null);
    // Stable per mount: the drawer keys drafts by it, so a remount must not
    // silently inherit the previous editor's conversation.
    const editorIdRef = useRef(`ed_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`);
    const editorId = editorIdRef.current;
    // The handle is registered once, but its closures must see live state --
    // a ref keeps them current without re-registering on every render.
    const actionsRef = useRef({});
    let bottomStyle = 40;

    const { showRewriteAiCard: showCard } = writingAssistantStoreData;
    const setShowCard = val => writingAssistantStoreData.setShowRewriteAiCard(val);
    const [text, setText] = useState("");
    const [loader, setLoader] = useState(false);
    const [prompts, setPrompts] = useState(writingAssistantStoreData.prompts);
    const [emotions, setEmotions] = useState(
      writingAssistantStoreData.emotions
    );
    const [promptsLoader, setPromptLoader] = useState(
      writingAssistantStoreData.promptsLoader
    );
    const [scoreLoader, setScoreLoader] = useState(false);
    const [showReadabilityScore, setShowReadabilityScore] = useState(false);
    const [score, setScore] = useState({});
    const [previousContent, setPreviousContent] = useState("");
    const [staticHeight, setStaticHeight] = useState(bottomStyle);
    const [typewriteEffect, setTypewriteEffect] = useState(false);

    const resetState = () => {
      setShowCard(false);
      setText("");
      setLoader(false);
      resetPromots();
      setPromptLoader(false);
      if (!fromTaskPage) {
        writingAssistantStoreData.setAttachmentUpdated(false);
        writingAssistantStoreData.setInputValue({ ...defaultInputValue });
      }
    };

    const constructContentText = (
      options = "",
      dom = "<br>",
      isSpintax = false
    ) => {
      let content = "";
      let subject = "";
      if (options?.length > 0) {
        const regex =
          /^[\n\r]*(?:Subject Line|Subject|Line):\s*([\s\S]*?)\s*[\n\r]+([\s\S]*)/gm;
        const match = regex.exec(options);
        if (match?.length > 0) {
          subject = match?.[1];
        }
        let finalContent = match?.[2] || options;

        let transformedContent = finalContent;
        if (!isSpintax) {
          const transformDoubleBracesToSingle = finalContent
            ?.replaceAll("\n\n", "\n")
            ?.replace(/\{{2,}/g, "{")
            ?.replace(/\}{2,}/g, "}");
          transformedContent = transformDoubleBracesToSingle
            ?.replace(/[}]/g, "}}")
            ?.replace(/[{]/g, "{{");
        }

        content = transformedContent
          ?.replace(/^\s+|\s+$/g, "")
          ?.replace(/(?:\r\n|\r|\n)/g, "<div><br ></div>");
      }
      return { content, subject };
    };

    const handleErrorResponseMessage = (code = "", data = {}) => {
      const contentObj = {
        411: "Oops! No results found. Please try again with a different prompt.",
        412: "Oops! You've reached your request limit. Please try again later.",
        429: "Oops! You're making too many requests. Please wait & try again later.",
        430: "Oops! You've reached the OpenAI request limits. Please wait & try again later.",
        500: "Something went wrong. Please try again.",
        501: "Something went wrong. Please try again."
      };
      let responseCode =
        (code
          ? code
          : !data || (data && Object.keys(data)?.length < 1 && 411)) || 500;
      toasterState.setToastMsg(contentObj[responseCode || 500], "fail");
    };

    // Applying a result is split out of fetching it so compose mode can show the
    // draft first and let the user decide -- the inline card still fetches and
    // applies in one go.
    const applyAiContent = ({
      inputValue = {},
      output = "",
      isSpintax = false,
      apiSubject = null
    } = {}) => {
      writingAssistantStoreData.setTypewriteAnimation(true);
      writingAssistantStoreData.setSpintaxSeed(Date.now());
      if (fromTaskPage) {
        writingAssistantStoreData.setTaskBodyContent(output);
      } else {
        writingAssistantStoreData.setInputValue(inputValue);
        writingAssistantStoreData.setBodyContent(output);
        if (isSpintax) {
          if (output) {
            const parsedBody = Utils.parseSpintax(output);
            const formattedSample = parsedBody
              ?.replace(/^\s+|\s+$/g, "")
              ?.replace(/(?:\r\n|\r|\n)/g, "<div><br ></div>");
            writingAssistantStoreData.setSampleParsedSpintax(formattedSample);
          }
          if (apiSubject) {
            const parsedSubject = Utils.parseSpintax(apiSubject);
            writingAssistantStoreData.setSampleParsedSubject(parsedSubject);
          }
        }
      }
      Utils.mixpanelTrack(MXP_EVENT.CHAT_GPT_AI_GENERATED);
      updateContentCbk && updateContentCbk(inputValue, "rewrite");
    };

    const getAiContent = async (
      payload = {},
      insertSignature = false,
      isRewrite = false,
      emotionId = "",
      apply = true
    ) => {
      if (payload && Object.keys(payload)?.length > 0) {
        setLoader(true);
        writingAssistantStoreData.clearSamples();
        let res = null;
        const isSpintax = emotionId?.toLowerCase() === "spintax";
        if (isSpintax) {
          let spintaxPayload = {
            input: payload.content,
            prompt: payload.prompt,
            emailType: replyAsThread ? "SEND_AS_REPLY" : "SEND_AS_NEW_THREAD"
          };
          if (!replyAsThread) {
            spintaxPayload.emailSubject = emailSubject;
          }
          res = await writingAssistantStoreData.generateSpintax(spintaxPayload);
        } else {
          res = await writingAssistantStoreData.generateContent(payload);
        }

        let output = isSpintax
          ? res?.data?.body || res?.data?.output
          : res?.data?.options;
        const apiSubject = isSpintax ? res?.data?.subject : null;

        let validResponse = isSpintax
          ? !!output
          : output?.length > 0 ||
            (output && Object.keys(output)?.length > 0);

        if (validResponse) {
          let inputValue = constructContentText(output, "<br >", isSpintax);
          if (isSpintax && apiSubject) {
            inputValue.subject = apiSubject;
          }
          inputValue.insertSignature = insertSignature; // ONE OFF EMAIL => insert signature
          inputValue.isRewrite = isRewrite; // Rewrite exisitng content
          const result = { inputValue, output, isSpintax, apiSubject };
          apply && applyAiContent(result);
          setLoader(false);
          return result;
        } else if (res?.response?.status === 401) {
          Utils.showLoggedOutPopup();
        } else if (res?.response?.status === 403) {
          WritingAssistantUtils.upgradePopup(inFreePlan, planName);
        } else {
          handleErrorResponseMessage(res?.response?.status, res?.data);
        }
        setLoader(false);
      }
    };

    const generateContent = async () => {
      if (text?.trim()?.length > 0) {
        Utils.mixpanelTrack(MXP_EVENT.CHAT_GPT_AI_HELP_ME_WRITE_CLICKED);
        getAiContent(
          {
            prompt: text
          },
          true, // ONE OFF EMAIL => insert signature
          false // Rewrite existing content
        );
        setText("");
        resetPromots();
      }
    };

    const handleSubmit = (event = "") => {
      Utils.isSubmitKeyAction(event) && generateContent();
    };

    // apply=false makes this return the draft instead of writing it, so compose
    // mode gets the same word-count guard and the same spintax confirmation the
    // inline card has always had rather than a second copy of both.
    const rewriteContent = (
      isGenerateScore = false,
      prompt = "",
      emotionId = "",
      apply = true
    ) => {
      let content =
        (pageType?.toLowerCase() === "task"
          ? writingAssistantStoreData?.taskBodyContent
          : writingAssistantStoreData?.bodyContent) || "";
      let newLineContent = WritingAssistantUtils.replaceDomWithLineSeperator(
        content,
        "\n"
      );
      let plainText = WritingAssistantUtils.convertToPlainText(
        newLineContent,
        "/n"
      );
      let newContent = WritingAssistantUtils.addDotToString(
        plainText?.replaceAll("/n", "\n") || plainText || ""
      );
      newContent = newContent?.replace(/ +(?= )/g, "");
      let contentLength =
        WritingAssistantUtils.getContentWordLength(newContent);
      Utils.mixpanelTrack(MXP_EVENT.CHAT_GPT_AI_SCORE_REWRITE_AI_CLICKED);
      if (contentLength > 10) {
        if (isGenerateScore) {
          return newContent;
        } else {
          let payload = { content: newContent };
          prompt && (payload.prompt = prompt);
          const SPINTAX_PATTERN = /\{([^{}|]*\|[^{}]*)\}/;
          if (
            emotionId?.toLowerCase() === "spintax" &&
            SPINTAX_PATTERN.test(newContent)
          ) {
            // Resolve on dismissal too. Without it a caller awaiting this sits
            // on a spinner forever when the user cancels the confirmation.
            return new Promise(resolve => {
              let settled = false;
              let dispose = null;
              const finish = value => {
                if (settled) return;
                settled = true;
                dispose && dispose();
                resolve(value);
              };
              confirmationPopupState.setPopupValues({
                content:
                  "Replace existing email? Your current variations will be replaced with new ones generated by AI.",
                actionBtnText: "Replace",
                callback: () => {
                  finish(getAiContent(payload, false, true, emotionId, apply));
                }
              });
              confirmationPopupState.setShowPopup(true);
              dispose = reaction(
                () => confirmationPopupState.showPopup,
                show => {
                  if (!show) finish(null);
                }
              );
            });
          }
          return getAiContent(payload, false, true, emotionId, apply);
        }
      } else {
        WritingAssistantUtils.showEmptyContentConfirmPrompt(
          "Please add more content to your email body to let AI generate different variations of your email."
        );
        return null;
      }
    };

    const generateScore = async () => {
      if (writingAssistantEnabled) {
        Utils.mixpanelTrack(MXP_EVENT.CHAT_GPT_AI_SCORE_CLICKED);
        let content = rewriteContent(true);
        if (content === previousContent) {
          setShowReadabilityScore(true);
          return;
        }
        showReadabilityScore && setShowReadabilityScore(false);
        if (content) {
          !scoreLoader && setScoreLoader(true);
          let res = await writingAssistantStoreData.generateScore({ content });
          if (res?.data?.options) {
            setPreviousContent(content);
            let tempScoreObj =
              JSON.parse(res?.data?.options?.replaceAll("\n", "")) || {};
            setScore(tempScoreObj);
            setShowReadabilityScore(true);
          } else if (res?.response?.status === 401) {
            Utils.showLoggedOutPopup();
          } else if (res?.response?.status === 403) {
            WritingAssistantUtils.upgradePopup(inFreePlan, planName);
          } else {
            handleErrorResponseMessage(res?.response?.status || 500);
          }
          setScoreLoader(false);
        }
      } else {
        WritingAssistantUtils.upgradePopup(inFreePlan, planName);
      }
    };

    const composeActive =
      COMPOSE_IN_COPILOT &&
      !userDetail?.featuresDisabled?.includes("COPILOT") &&
      copilotEditorStore.target?.id === editorId;

    // showRewriteAiCard is set from three places -- the wand, the readability
    // score's "Rewrite with AI", and the Variations dropdown's "Write with AI".
    // Redirecting only the wand left the other two raising the old inline card
    // on top of the drawer, so the redirect belongs here, where they converge.
    useEffect(() => {
      if (composeActive && showCard) {
        setShowCard(false);
        copilotDrawerStore.openFor("email_editor", editorId);
      }
    }, [composeActive, showCard]);

    const openChatGptSearchBox = () => {
      Utils.mixpanelTrack(MXP_EVENT.CHAT_GPT_AI_CLICKED);
      if (!loader && !scoreLoader) {
        if (writingAssistantEnabled) {
          // Compose mode: the same prompt, asked in the copilot instead of a
          // card of its own. Falls back to the card wherever the drawer is not
          // mounted, so no surface loses the wand.
          if (composeActive) {
            copilotDrawerStore.openFor("email_editor", editorId);
          } else {
            setShowCard(true);
          }
        } else {
          WritingAssistantUtils.upgradePopup(inFreePlan, planName);
        }
      }
    };

    const resetPromots = () => {
      if (prompts?.length > 0) {
        let tempList = [...prompts].map(item => {
          item.show = false;
          return item;
        });
        setPrompts(tempList);
      }
    };

    const adjustChatGptHeight = () => {
      if (!fromTaskPage) {
        setTimeout(() => {
          const chatGptDom = document.getElementById("chatGptEditorSection");
          if (chatGptDom) {
            let attachmentHeight =
              document.getElementById("editorFileAttachment")?.clientHeight ||
              0;
            setStaticHeight(bottomStyle + attachmentHeight);
            writingAssistantStoreData.setAttachmentUpdated(false);
          }
        }, 50);
      }
    };

    actionsRef.current = {
      generate: prompt =>
        getAiContent({ prompt }, true, false, "", false),
      rewrite: (prompt, emotionId) =>
        Promise.resolve(rewriteContent(false, prompt, emotionId, false)),
      apply: result => applyAiContent(result),
      // Scoped edit: the same endpoint, but the payload is the selected
      // fragment rather than the whole body, so the model rewrites only what
      // was highlighted and the rest of the email is never at risk.
      editSelection: (text, prompt) =>
        getAiContent(
          { content: text, prompt: prompt || "Rewrite this passage." },
          false,
          true,
          "",
          false
        ),
      // Passive check -- unlike rewriteContent it must not pop the "add more
      // content" prompt just because the drawer asked what it can offer.
      hasBody: () => {
        const content =
          (pageType?.toLowerCase() === "task"
            ? writingAssistantStoreData?.taskBodyContent
            : writingAssistantStoreData?.bodyContent) || "";
        const plainText = WritingAssistantUtils.convertToPlainText(
          WritingAssistantUtils.replaceDomWithLineSeperator(content, "\n"),
          "/n"
        );
        return WritingAssistantUtils.getContentWordLength(plainText || "") > 10;
      }
    };

    const handleRewriteAIAction = () => {
      setShowReadabilityScore(false);
      if (composeActive) {
        copilotEditorStore.requestRewrite();
        copilotDrawerStore.openFor("email_editor", editorId);
        return;
      }
      setShowCard(true);
      rewriteContent(false);
    };

    const removeChatGptEditorScrollClass = (editorRef = "") => {
      if (editorRef) {
        let hasNormalClass = editorRef.root.classList.contains(
          "chatGptEnabledNormal"
        );
        let hasLargeClass = editorRef.root.classList.contains(
          "chatGptEnabledLarge"
        );
        let hasSmallClass = editorRef.root.classList.contains(
          "chatGptEnabledSmall"
        );
        if (hasLargeClass) {
          editorRef.root.classList.remove("chatGptEnabledLarge");
        } else if (hasSmallClass) {
          editorRef.root.classList.remove("chatGptEnabledSmall");
        } else if (hasNormalClass) {
          editorRef.root.classList.remove("chatGptEnabledNormal");
        }
        return true;
      }
    };

    const chatGptEditorScrollClass = (editorRef = "") => {
      if (editorRef) {
        let height = 0;
        if (!fromTaskPage) {
          let attachmentHeight =
            document.getElementById("editorFileAttachment")?.clientHeight || 0;
          height += attachmentHeight;
        }
        let value = height > 50 ? "Large" : height > 0 ? "Small" : "Normal";
        if (showCard && !typewriteEffect && !loader) {
          let success = removeChatGptEditorScrollClass(editorRef);
          success && editorRef.root.classList.add(`chatGptEnabled${value}`);
        } else {
          removeChatGptEditorScrollClass(editorRef);
        }
      }
    };

    // The drawer outlives this editor, so it gets a handle rather than a guess at
    // which editor is on screen. Generation still runs through getAiContent, so
    // the subject parsing, merge tags, paywall and typewriter stay in one place.
    useEffect(() => {
      if (!COMPOSE_IN_COPILOT || !writingAssistantEnabled) return undefined;
      copilotEditorStore.register({
        id: editorId,
        label: composeLabel || "this email",
        generate: prompt => actionsRef.current.generate(prompt),
        rewrite: (prompt, emotionId) =>
          actionsRef.current.rewrite(prompt, emotionId),
        apply: result => actionsRef.current.apply(result),
        editSelection: (text, prompt) =>
          actionsRef.current.editSelection(text, prompt),
        hasBody: () => actionsRef.current.hasBody()
      });
      return () => copilotEditorStore.unregister(editorId);
    }, [writingAssistantEnabled, composeLabel]);

    useEffect(() => {
      adjustChatGptHeight();
      return () => {
        resetState();
        setShowReadabilityScore(false);
        setScore({});
        setPreviousContent("");
        setStaticHeight(bottomStyle);
      };
    }, []);

    useEffect(() => {
      chatGptEditorScrollClass(editorRef);
    }, [
      typewriteEffect,
      loader,
      showCard,
      writingAssistantStoreData?.attachmentUpdated
    ]);

    useEffect(() => {
      writingAssistantStoreData?.attachmentUpdated && adjustChatGptHeight();
    }, [writingAssistantStoreData?.attachmentUpdated]);

    useEffect(() => {
      if (!writingAssistantStoreData?.promptsLoader) {
        setPrompts([...(writingAssistantStoreData?.prompts || [])]);
        setEmotions([...(writingAssistantStoreData?.emotions || [])]);
        setPromptLoader(writingAssistantStoreData?.promptsLoader || false);
      }
    }, [writingAssistantStoreData?.promptsLoader]);

    useEffect(() => {
      if (typewriteEffect !== writingAssistantStoreData.hideChatGpt) {
        setTypewriteEffect(writingAssistantStoreData.hideChatGpt);
      }
    }, [writingAssistantStoreData?.hideChatGpt]);

    return (
      !typewriteEffect && (
        <div
          className="chatGptWrapper"
          style={{
            bottom: `${staticHeight}px`
          }}
        >
          <RewriteAiContext.Provider
            value={{
              showCard,
              setShowCard,
              text,
              setText,
              loader,
              handleSubmit,
              setLoader,
              generateContent,
              rewriteContent,
              prompts,
              setPrompts,
              promptsLoader,
              resetPromots,
              emotions,
              resetState,
              inputBoxRef,
              generateScore,
              scoreLoader,
              setScoreLoader,
              score,
              setScore,
              showReadabilityScore,
              setShowReadabilityScore,
              handleRewriteAIAction
            }}
          >
            {!showCard ? (
              <div className="iconBtnWrapper">
                <ScoreBot />
                <WandButton
                  action={openChatGptSearchBox}
                  scoreLoader={scoreLoader}
                />
              </div>
            ) : loader ? (
              <ChatGptLoader />
            ) : (
              <RewriteAiInputCard />
            )}
          </RewriteAiContext.Provider>
        </div>
      )
    );
  }
);

export default RewriteAi;
export { RewriteAiContext };
